import React, { useState, useEffect, useContext } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../Context/AppContextProvide';
import { setUser as persistUser } from '../../../helpers';
import { getVersion, updateSeenVersion, getHelpCatalog } from '../../../Actions';
import { CHANGELOG } from './changelog';
import {
  startTour,
  startNavTour,
  startHeaderSearchTour,
  startQrTour,
  resumePrintedIndicatorTour,
  startDuplicatesTour,
  startFeatureRequestsTour,
  startHelpCatalogTour,
  startTimezoneTour,
  startQrStartPositionTour,
  startPriceTierTour,
  startDiscontinueTour,
  startBulkPriceTour,
  startQueueQuantityTour,
  startMultiSampleCheckoutTour,
  startCalcSummaryTour,
  startCostAdjustmentsTour,
} from './tours';

export default function WhatsNewModal() {
  const { isLoggedOut, user, setUser, whatsNewOpen, setWhatsNewOpen, setTourMenuOpen } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [release, setRelease] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  // Help-video catalog, keyed by slug (catalog `key`), + the video currently playing.
  const [videosBySlug, setVideosBySlug] = useState({});
  const [activeVideo, setActiveVideo] = useState(null);
  const history = useHistory();

  useEffect(() => {
    if (isLoggedOut) return;

    getVersion()
      .then((result) => {
        const appVersion = result?.data?.app_version;
        if (!appVersion) return;
        if (!CHANGELOG[appVersion]) return;

        setCurrentVersion(appVersion);
        setRelease(CHANGELOG[appVersion]);

        // Don't auto-prompt hidden/internal users with the "What's Changed" modal (they can still
        // open it manually from the menu).
        if (user?.last_seen_version !== appVersion && user?.onboarding_completed && !user?.is_hidden) {
          setOpen(true);
        }
      })
      .catch(() => {});
  }, [isLoggedOut]);

  useEffect(() => {
    if (whatsNewOpen) {
      setOpen(true);
      setWhatsNewOpen(false);
    }
  }, [whatsNewOpen]);

  // Only load the help catalog when this release actually references a video. The
  // catalog already returns approved+active videos with absolute URLs, so a slug that
  // resolves here is safe to show; unresolved slugs simply hide the button.
  useEffect(() => {
    if (!release || !release.features?.some((f) => f.video)) return;
    getHelpCatalog()
      .then((r) => {
        const map = {};
        (r.data?.videos || []).forEach((v) => {
          if (v.key) map[v.key] = v;
        });
        setVideosBySlug(map);
      })
      .catch(() => {});
  }, [release]);

  function handleClose() {
    if (currentVersion) {
      updateSeenVersion(currentVersion).catch(() => {});
      const updatedUser = { ...user, last_seen_version: currentVersion };
      setUser(updatedUser);
      persistUser(updatedUser);
    }
    setOpen(false);
  }

  function handleTour() {
    handleClose();
    startTour(history, setTourMenuOpen);
  }

  function handleFeatureTour(tourKey) {
    handleClose();
    switch (tourKey) {
      case 'nav':              startNavTour(history, setTourMenuOpen); break;
      case 'headerSearch':     startHeaderSearchTour(history, setTourMenuOpen); break;
      case 'qr':               startQrTour(history); break;
      case 'printedIndicator': resumePrintedIndicatorTour(history); break;
      case 'duplicates':       startDuplicatesTour(history); break;
      case 'featureRequests':  startFeatureRequestsTour(history, setTourMenuOpen); break;
      case 'helpCatalog':      startHelpCatalogTour(history, setTourMenuOpen); break;
      case 'timezone':         startTimezoneTour(history); break;
      case 'qrStartPosition':  startQrStartPositionTour(history); break;
      case 'priceTiers':       startPriceTierTour(history); break;
      case 'discontinue':      startDiscontinueTour(history); break;
      case 'bulkPrice':        startBulkPriceTour(history); break;
      case 'queueQuantity':    startQueueQuantityTour(history); break;
      case 'multiSampleCheckout': startMultiSampleCheckoutTour(history); break;
      case 'calcSummary':      startCalcSummaryTour(history); break;
      case 'costAdjustments':  startCostAdjustmentsTour(history); break;
      default: break;
    }
  }

  if (!release) return null;

  const canTour = user?.access_type_cd !== 'c';

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography>
        <Typography variant="h6">{release.title}</Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
          {release.subtitle}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {release.features.map((feature, i) => (
          <div key={i}>
            {i > 0 && <Divider style={{ margin: '12px 0' }} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <Typography variant="subtitle2" gutterBottom style={{ flex: 1 }}>
                {feature.title}
              </Typography>
              {feature.video && videosBySlug[feature.video] && (
                <Button
                  size="small"
                  color="primary"
                  style={{ fontSize: 11, padding: '2px 8px', flexShrink: 0, marginTop: -2 }}
                  onClick={() => setActiveVideo(videosBySlug[feature.video])}
                >
                  ▶ Video
                </Button>
              )}
              {canTour && feature.tourKey && (
                <Button
                  size="small"
                  color="primary"
                  style={{ fontSize: 11, padding: '2px 8px', flexShrink: 0, marginTop: -2 }}
                  onClick={() => handleFeatureTour(feature.tourKey)}
                >
                  Tour →
                </Button>
              )}
            </div>
            <Typography variant="body2" color="textSecondary">
              {feature.description}
            </Typography>
          </div>
        ))}
      </DialogContent>

      <DialogActions style={{ justifyContent: 'space-between' }}>
        {canTour && (
          <Button color="primary" onClick={handleTour}>
            Full Tour
          </Button>
        )}
        <Button variant="contained" color="primary" onClick={handleClose}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>

    {/* Video player — opens over the What's New dialog, which stays open behind it. */}
    <Dialog open={!!activeVideo} onClose={() => setActiveVideo(null)} maxWidth="md" fullWidth>
      {activeVideo && (
        <>
          <DialogTitle disableTypography>
            <Typography variant="h6">{activeVideo.title}</Typography>
          </DialogTitle>
          <DialogContent>
            <video
              controls
              autoPlay
              width="100%"
              src={activeVideo.video_url}
              style={{ borderRadius: 4, display: 'block' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActiveVideo(null)}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
    </>
  );
}
